// THE TABLE CARD FEED: what the DM can do with a roll card that lands in
// Roll20 chat — see whether it hit, roll an attack nobody rolled, open the
// Who-is-affected dialog, or apply its damage or healing to one target.
//
// Lifted out of Initiative.tsx on 2026-10-03 (S5-1). Logic and markup moved
// verbatim: this commit is a move, not a rewrite. It is shaped like
// useEffectsEngine — a hook that owns its own state and hands back a panel —
// because that pattern is already the house style for a subsystem with a UI.
//
// What it owns: the attack roll it makes when a card states a bonus and nobody
// rolled it, the hit-vs-AC check, the per-card target choice, and the apply row
// (which reaches a creature's HP bar as well as a character's sheet). What it
// does NOT own is the card list itself: the chat listener that fills it also
// feeds the aura tracker and the effects engine, so it stays on the page.
import { useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { newRequestId, request, useBridgeStatus } from "../bridge";
import { useStore } from "../state/store";
import { applyHit, MAX_HP_DRAIN } from "../state/ddbLive";
import { automation } from "../state/automation";
import { applyAmountOf as cardApplyAmountOf, blockedByMiss, healsOnly as cardHealsOnly, landing } from "../state/cardApply";
import { affectVerb, decideAffect } from "../state/affect";
import { barFor, npcBarLayout } from "../state/tokenBars";
import { barNumber, barUpdate, barValue, readMap } from "../state/auraReach";
import { normLoose as norm } from "../state/text";
import { defeatNpc } from "../state/roll20Bars";
import { withBarLock } from "../state/barQueue";
import { signed, type Character } from "../state/types";
import type { Die } from "../state/rollDice";
import type { Roll20Link } from "./TableSettings";

/** One evaluated roll card as the connector reported it. `dice` is the row's individual
 *  die faces parsed from Roll20's tooltip (state/rollDice.ts) — empty unless the connector
 *  relayed a breakdown; read it for rules the sum cannot answer (a matched-d8 bounce, a crit). */
export interface Card { id: string; name: string; rows: { label: string; total: number | null; value: string; crit: boolean; fumble: boolean; dice: Die[] }[]; at: number; applied?: string }

export interface CardFeedArgs {
  cards: Card[];
  setCards: Dispatch<SetStateAction<Card[]>>;
  roster: Character[];
  entries: { id: string; name: string; kind: string; tokenId?: string }[];
  allyKey: (e: { id: string; tokenId?: string; name: string }) => string;
  link: Roll20Link | undefined;
  postChat: (lines: { text: string; style?: string; speaker?: string; whisperTo?: string; gm?: boolean }[]) => void;
  openAffect: (card: Card) => void;
  isAlly: (e: { id: string; tokenId?: string; name: string; kind?: string }) => boolean;
  d20: () => number;
}

export function useCardFeed({ cards, setCards, roster, entries, allyKey, link, postChat, openAffect, isAlly, d20 }: CardFeedArgs): { panel: ReactNode } {
  const { layout, update, logActivity } = useStore();
  const rollsToChat = layout.rollsToChat !== false;
  const announceStyle = String(layout.announceStyle || "desc");
  const announceSpeaker = String(layout.announceSpeaker || "Turn Order");
  const bridge = useBridgeStatus();
  const auraChat = layout.auraEmotes !== false;
  const canAnnounce = Boolean(bridge.capabilities?.roll20ChatAnnounce);
  /* The DM's edits to a card, per card: what to apply and whether it drains a
     maximum. Both start from the card itself and are only here once changed, so
     a card nobody has touched carries no state at all. */
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [drains, setDrains] = useState<Record<string, boolean>>({});
  /* Hit or heal: the predicate lives in state/cardApply.ts so it can be tested.
     It was inline here and wrong in three ways at once without anything noticing. */
  const healsOnly = (card: Card) => cardHealsOnly(card.rows);
  const applyAmountOf = (card: Card) => cardApplyAmountOf(card.rows);
  // Hit / miss: the card's attack total against the chosen target's AC — a
  // party member's AC from the sheet, a monster's from its AC bar on the map.
  // AN ATTACK NOBODY ROLLED. A statblock action that parsed as an attack when
  // it was written to the Roll20 journal posts a To hit row. One that did not —
  // a "Ranged Spell Attack" the parser read as plain prose, a homebrew action,
  // anything hand-written — posts its description instead, with the damage
  // rolled inline and the attack bonus left sitting there as text. The table
  // could see 25 force damage and had no way to know whether it hit.
  //
  // So: when a card states a bonus to hit and carries no attack roll, the DM
  // Screen offers to roll it, posts the roll to Roll20 so the table sees it
  // happen, and hands the result to the same AC check every other attack uses.
  const statedBonus = (card: Card): number | null => {
    const text = [card.name, ...card.rows.map((r) => `${r.label} ${r.value}`)].join(" ");
    const m = text.match(/([+-]\s*\d+)\s*(?:to\s*hit|to-hit)/i);
    return m ? Number(m[1].replace(/\s+/g, "")) : null;
  };
  const [rolledAttacks, setRolledAttacks] = useState<Record<string, { die: number; bonus: number }>>({});
  const attackOf = (card: Card) => {
    const row = card.rows.find((r) => /attack|to hit|\bhit\b/i.test(r.label) && !/damage|dmg/i.test(r.label) && r.total !== null);
    if (row) return row;
    const mine = rolledAttacks[card.id];
    if (!mine) return null;
    const total = mine.die + mine.bonus;
    return { label: "To hit", value: String(total), total, crit: mine.die === 20, fumble: mine.die === 1 };
  };
  const rollStatedAttack = (card: Card) => {
    const bonus = statedBonus(card); if (bonus === null) return;
    const die = d20();
    setRolledAttacks((m) => ({ ...m, [card.id]: { die, bonus } }));
    const total = die + bonus;
    const note = die === 20 ? " — natural 20" : die === 1 ? " — natural 1" : "";
    logActivity({ text: `${card.name || "Attack"}: ${total} to hit`, meta: `d20 ${die} ${signed(bonus)}${note} — rolled here because the card carried no attack roll`, tone: die === 1 ? "amber" : "violet" });
    if (rollsToChat) void postChat([{ text: `${card.name || "Attack"} — ${total} to hit (d20 ${die} ${signed(bonus)})${note}`, style: announceStyle, speaker: announceSpeaker }]);
  };
  const [hitTargets, setHitTargets] = useState<Record<string, string>>({});
  const [npcAc, setNpcAc] = useState<Record<string, number | null>>({});
  const targetAc = (key: string): number | null => {
    if (key.startsWith("c:")) { const c = roster.find((x) => x.id === key.slice(2)); return c ? Number(c.ac || 0) || null : null; }
    return npcAc[key] ?? null;
  };
  const pickHitTarget = (cardId: string, key: string) => {
    setHitTargets((m) => ({ ...m, [cardId]: key }));
    if (!key.startsWith("c:") && npcAc[key] === undefined && link) {
      const e = entries.find((x) => allyKey(x) === key);
      void readMap(link).then((map) => { const t = map.tokens.find((x) => (e?.tokenId && x.id === e.tokenId) || (e && norm(x.name) === norm(e.name))); const bar = barFor(npcBarLayout(layout), "ac"); const v = t && bar ? Number(barValue(t, bar).match(/^-?\d+/)?.[0]) : NaN; setNpcAc((m) => ({ ...m, [key]: Number.isFinite(v) && v > 0 ? v : null })); }).catch(() => setNpcAc((m) => ({ ...m, [key]: null })));
    }
  };
  /* ONE CLICK, ONE ATTACK (standing rule 9).

     This used to be three controls and three clicks: choose a target in the `vs`
     selector, press "Apply hit…", then confirm in a second row that asked for the
     target again. The target was carried over, so the second question had a right
     answer already filled in — which is the definition of a step that should not
     exist. Now the target is chosen once and the button applies.

     What is NOT automatic, deliberately: nothing applies without the DM naming a
     target. Picking who gets hit is a decision, and the console does not take
     decisions. */
  const targetNameOf = (key: string): string => {
    if (key.startsWith("c:")) return roster.find((c) => c.id === key.slice(2))?.name || "them";
    return entries.find((e) => allyKey(e) === key)?.name || "it";
  };
  const amountFor = (card: Card) => amounts[card.id] ?? (applyAmountOf(card) ?? 0);
  const drainFor = (card: Card) => drains[card.id] ?? (!healsOnly(card) && MAX_HP_DRAIN.test(card.name));
  /** Would the AC check stop this? Only when there is an attack roll AND a target. */
  const missed = (card: Card) => blockedByMiss(attackOf(card), hitTargets[card.id] ? targetAc(hitTargets[card.id]) : null, automation(layout, "hitVsAc"));

  const applyNow = (card: Card) => {
    const key = hitTargets[card.id]; if (!key) return;
    const heal = healsOnly(card);
    const amount = amountFor(card);
    /* A creature: its hit points live on its Roll20 token, not on a sheet. */
    if (!key.startsWith("c:")) {
      const e = entries.find((x) => allyKey(x) === key); if (!e) return;
      void writeNpcHp(e, heal ? amount : -amount, card.name || (heal ? "healing" : "a hit"));
      setCards((list) => list.map((x) => (x.id === card.id ? { ...x, applied: e.name } : x)));
      return;
    }
    const c = roster.find((x) => x.id === key.slice(2)); if (!c) return;
    if (heal) {
      /* Healing from 0 clears the death saves, as every other healing path here does. */
      const was = Number(c.hp || 0);
      const hp = Math.min(Number(c.maxHp || 0), was + amount);
      update((l) => ({ ...l, characters: l.characters.map((x) => (x.id === c.id ? { ...x, hp, ...(was <= 0 ? { deathSuccesses: 0, deathFailures: 0 } : {}) } : x)) }));
      logActivity({ text: `${card.name || "A spell"} heals ${c.name}`, meta: `${hp - was} healed · ${was} → ${hp}`, tone: "green" });
    } else {
      const next = applyHit(c, amount, drainFor(card));
      update((l) => ({ ...l, characters: l.characters.map((x) => (x.id === c.id ? { ...x, ...next } : x)) }));
      logActivity({ text: `${card.name || "Attack"} hits ${c.name}`, meta: `${amount} damage${drainFor(card) ? ", maximum reduced" : ""}`, tone: "red" });
    }
    setCards((list) => list.map((x) => (x.id === card.id ? { ...x, applied: c.name } : x)));
  };
  /* DAMAGE OR HEALING ONTO A CREATURE'S HP BAR.

     A creature is not a roster character: its hit points live on its Roll20 token,
     in whichever bar the DM mapped to HP. applyAffect has always known how to do
     this; the quick-apply row did not, and so its target list held only party
     members — an attack could be applied to your own side and nothing else, which
     is how the DM found it.

     Shared rather than copied. A second rule for writing a creature's hit points
     is exactly the kind of drift that has cost this project a week of fixes. */
  const writeNpcHp = async (e: { id: string; name: string; tokenId?: string }, delta: number, why: string): Promise<boolean> => withBarLock(async () => {
    /* Read and write as one. Two hits landing inside a single Roll20 round trip
       both read the same value and the second overwrites the first, so a hit
       vanishes with both logged as applied. */
    if (!link) { logActivity({ text: `${e.name}: not applied`, meta: "no Roll20 game is linked — use Link a game on the Initiative page first", tone: "amber" }); return false; }
    const bars = npcBarLayout(layout); const hpBar = barFor(bars, "hp");
    if (!hpBar) { logActivity({ text: `${e.name}: not applied`, meta: "no NPC bar is set to HP (Roll20 options)", tone: "amber" }); return false; }
    const map = await readMap(link).catch(() => null);
    const tok = map?.tokens.find((x) => (e.tokenId && x.id === e.tokenId) || norm(x.name) === norm(e.name));
    /* Two failures, two messages: a token that is not on this page and a bar with no
       number send the DM to different places. */
    if (!tok) { logActivity({ text: `${e.name}: not applied`, meta: "no token of that name on this page — is it on another page, or renamed?", tone: "amber" }); return false; }
    const have = barNumber(tok, hpBar);
    if (have === null) { logActivity({ text: `${e.name}: not applied`, meta: `its Bar ${hpBar} holds no number, it holds "${barValue(tok, hpBar)}" — set that bar on the token, or map a different one under Roll20 options`, tone: "amber" }); return false; }
    if (!Number.isFinite(delta)) { logActivity({ text: `${e.name}: not applied`, meta: "the amount to apply is not a number — type the damage or healing in the box and try again", tone: "amber" }); return false; }
    const next = Math.max(0, have + delta);
    /* The boundary refuses anything that is not a finite number. Reaching here with
       one would mean the guards above were wrong — and a refused write is recoverable
       where a corrupted bar is not. */
    const barWrite = barUpdate({ id: tok.id || e.tokenId, name: e.name }, hpBar, next);
    if (!barWrite) { logActivity({ text: `${e.name}: not applied`, meta: `the new value was not a number (${have} + ${delta})`, tone: "amber" }); return false; }
    try { await request<any>({ type: "DM_SCREEN_ROLL20_BARS_REQUEST", requestId: newRequestId("quick-apply"), tabId: link.tabId, frameId: link.frameId, scope: "page", updates: [barWrite] }, { resultType: "DM_SCREEN_ROLL20_BARS_RESULT", timeoutMs: 20000 }); } catch { /* the log below still records what was intended */ }
    logActivity({ text: `${e.name}: ${why}`, meta: `Bar ${hpBar} ${have} → ${next}`, tone: delta < 0 ? "red" : "green" });
    /* Dropping a creature is the same event however the damage arrived. */
    if (next <= 0 && delta < 0 && automation(layout, "zeroHp")) void defeatNpc(link, { tokenId: tok?.id || e.tokenId, name: e.name }, { announce: auraChat && canAnnounce, style: String(layout.announceStyle || "desc"), speaker: String(layout.announceSpeaker || "") });
    return true;
  });

  const panel = (
    <>
      <span className="eyebrow">Table cards <span className="pill">{cards.length}</span></span>
      <ul className="feed cards">{cards.map((card) => <li key={card.id} className={card.applied ? "applied" : ""}>
        <time>{new Date(card.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
        <strong>{card.name || "Card"}</strong> {card.rows.map((r) => <span key={r.label} className={`pill ${r.crit ? "crit" : r.fumble ? "fumble" : ""}`}>{r.label} {r.total ?? r.value}</span>)}
        {/* Target, verdict, amount, apply — one line, one click. */}
        {!card.applied && applyAmountOf(card) !== null && (() => {
          const atk = attackOf(card);
          const key = hitTargets[card.id] || "";
          const ac = key ? targetAc(key) : null;
          const lands = landing(atk, ac);
          const heal = healsOnly(card);
          const amount = amountFor(card);
          const blocked = missed(card);
          return (
            <span className="apply-line">
              <select className="xs" value={key} aria-label={`Who ${card.name || "this card"} is applied to`} onChange={(e) => pickHitTarget(card.id, e.target.value)}>
                <option value="">{heal ? "heal…" : "target…"}</option>
                {entries.map((e) => <option key={e.id} value={e.kind === "pc" ? `c:${e.id}` : allyKey(e)}>{e.name}</option>)}
              </select>
              {atk && key && <span className={`pill ${lands === "unknown" ? "" : lands === "hit" ? "crit" : "fumble"}`}>{ac === null ? "AC ?" : `AC ${ac}`} · {atk.crit ? "natural 20 — hit" : atk.fumble ? "natural 1 — miss" : lands === "unknown" ? "…" : lands}</span>}
              {key && <>
                <label className="field small" title={heal ? "How much healing to apply; the card's own roll unless you change it" : "How much damage to apply; the card's own roll unless you change it"}>
                  {heal ? "heal" : "dmg"}<input type="number" className="xs" min={0} value={amount} aria-label={heal ? "Healing to apply" : "Damage to apply"} onChange={(e) => setAmounts((m) => ({ ...m, [card.id]: Math.max(0, Number(e.target.value) || 0) }))} />
                </label>
                {!heal && <label className="check small" title="Life drain and similar: the target's hit point maximum is reduced by the damage taken until a long rest"><input type="checkbox" checked={drainFor(card)} onChange={(e) => setDrains((m) => ({ ...m, [card.id]: e.target.checked }))} /> max</label>}
                <button className="small primary" disabled={blocked} title={blocked ? "It missed: the roll did not beat the target's AC" : `Apply to ${targetNameOf(key)}`} onClick={() => applyNow(card)}>
                  {heal ? `Heal ${amount}` : `Apply ${amount}`} → {targetNameOf(key)}
                </button>
              </>}
            </span>
          );
        })()}
        {!card.applied && !attackOf(card) && statedBonus(card) !== null && <button className="link small" title={`This card states ${signed(statedBonus(card)!)} to hit but nobody rolled it. Roll it here: the hit/miss check against the target’s AC then works as usual, and the roll is posted to Roll20 so the table sees it.`} onClick={() => rollStatedAttack(card)}>🎲 roll {signed(statedBonus(card)!)} to hit</button>}
        {!card.applied && (() => { const d = decideAffect(roster, card.name, card.rows); return d.ok ? <button className="small affect-go" onClick={() => void openAffect(card)} title="Pick the creatures, mark who saved, apply it to their sheets and tokens, and announce it in Roll20">◎ {affectVerb(d.mode)}…</button> : null; })()}
        {card.applied && <em className="muted small"> → {card.applied}</em>}
      </li>)}{!cards.length && <li className="muted">Attack cards posted to Roll20 (Compass rolls included) appear here with their rolled totals; apply a hit to a character from here.</li>}</ul>
    </>
  );
  return { panel };
}
